import { GlobalParagraphStyles } from '@/js/FroalaConfigurations'

/**
 * Handle CSS variable colors before Froala's default color application.
 * Returns false to prevent default behavior when CSS variable is detected,
 * otherwise returns undefined to allow default behavior.
 * 
 * Note: Froala uses 'applytextColor' and 'applybackgroundColor' commands
 * when a color is selected from the palette (not 'textColor'/'backgroundColor').
 */
export function FroalaBeforeCommand(self, cmd, param1, param2) {
  const isApplyTextColor = cmd === 'applytextColor'
  const isApplyBackgroundColor = cmd === 'applybackgroundColor'

  if (cmd === 'textColor') {
    const selection = self.selection.get();
    if (selection.rangeCount > 0) {
      self.selectedSearchRange = selection.getRangeAt(0);
      self.selectedSearchText = selection.getRangeAt(0).toString();
    }
  }

  // Only handle apply color commands with CSS variables
  if ((isApplyTextColor || isApplyBackgroundColor) && param1 && param1.startsWith('var(--')) {
    // Save the selection before applying
    if (self.selectedSearchRange) {
      const selectedText = self.selectedSearchText;

      if (selectedText) {
        // Check if the selection is already wrapped in a span
        const selectionElement = self.selection.element();
        const isExistingSpan = selectionElement &&
          selectionElement.tagName === 'SPAN' &&
          selectionElement.textContent === selectedText;

        if (isExistingSpan) {
          // Update the color on the existing span
          if (isApplyTextColor) {
            selectionElement.style.color = param1;
          } else {
            selectionElement.style.backgroundColor = param1;
          }
        } else {
          // Create a new span with the CSS variable color
          const span = document.createElement('span');
          if (isApplyTextColor) {
            span.style.color = param1;
          } else {
            span.style.backgroundColor = param1;
          }
          span.textContent = selectedText;

          // Replace selected text with the styled span
          self.selectedSearchRange.deleteContents();
          self.selectedSearchRange.insertNode(span);

          // Move cursor to end of inserted span
          const range = self.selection.get();
          const newRange = document.createRange();
          newRange.setStartAfter(span);
          newRange.collapse(true);
          range.removeAllRanges();
          range.addRange(newRange);
        }

        // Trigger save
        if (self.save && self.save.save) {
          self.save.save();
        }

        // Prevent Froala's default color handling
        return false;
      }
    }
  }

  if ((isApplyTextColor || isApplyBackgroundColor) && param1 === 'REMOVE') {
    // Handle removing color by unwrapping the span
    if (self.selectedSearchText) {
      const selectionElement = self.selection.element();

      // Check if selection is wrapped in a span
      if (selectionElement && selectionElement.tagName === 'SPAN') {
        const parent = selectionElement.parentNode;

        if (parent) {
          if (isApplyTextColor) {
            // Remove only the color style, keep the span if it has other styles
            selectionElement.style.removeProperty('color');
          } else {
            // Remove only the background-color style
            selectionElement.style.removeProperty('background-color');
          }

          // If the span has no more inline styles, unwrap it completely
          if (!selectionElement.getAttribute('style') || selectionElement.getAttribute('style').trim() === '') {
            // Replace span with its text content
            const textNode = document.createTextNode(selectionElement.textContent);
            parent.replaceChild(textNode, selectionElement);
          }

          // Trigger save
          if (self.save && self.save.save) {
            self.save.save();
          }

          // Clear stored selection
          self.selectedSearchText = null;
          self.selectedSearchRange = null;

          // Prevent Froala's default handling
          return false;
        }
      }
    }
  }

  // Allow default behavior for non-CSS-variable colors (hex, rgb, etc.)
  return undefined;
}

export function cleanFroalaContent(html) {
  // Regex to match froala-editor divs with or without data-v attributes
  const froalaRegex = /<div[^>]*class="[^"]*froala-editor[^"]*"[^>]*>/g;

  // Remove all opening froala-editor div tags
  let cleaned_html = html.replace(froalaRegex, '');

  // Remove all closing div tags that correspond to froala-editor divs
  cleaned_html = cleaned_html.replace(/<\/div>/g, '');

  // Remove empty HTML tags (any tag with nothing between opening and closing tags)
  // const emptyTagsRegex = /<([a-zA-Z])[^>]*>\s*<br\s*\/?>\s*<\/([a-zA-Z])>/g;
  // cleaned_html = cleaned_html.replace(emptyTagsRegex, '');

  return cleaned_html.trim();
}


export function FroalaAfterCommand(self, cmd, param1, param2, paragraphFontStyles) {
  const isClearFormatting = cmd === 'clearFormatting'

  const isCustomTextColor = cmd === 'customTextColor'
  const isTextColor = cmd === 'textColor'
  const isBackgroundColor = cmd === 'backgroundColor'
  const isCustomBackgroundColor = cmd === 'customBackgroundColor'

  if (isClearFormatting) {
    let paragraphString = self.selection.element()
    for (let style of GlobalParagraphStyles) {
      if (style.name === 'font-style-normalText') {
        if (!self?.node?.hasClass(paragraphString, style.name)) {
          self.paragraphFormat.apply(style.tag)
          self.paragraphStyle.apply(style.name)
        }
      } else {
        if (self?.node?.hasClass(paragraphString, style.name)) {
          // check if it contains it, then remove
          self.paragraphStyle.apply(style.name)
          self.paragraphFormat.apply(style.tag)
        }
      }
    }
  }

  if (isTextColor || isBackgroundColor) {
    // For some reason selected text is only retained here, so we commit it to state
    // Note: CSS variable colors are handled in FroalaBeforeCommand, so this only runs for non-CSS-variable colors
    const selection = self.selection.get();
    if (selection.rangeCount > 0 && !self.selectedSearchText) {
      self.selectedSearchRange = selection.getRangeAt(0);
      self.selectedSearchText = selection.getRangeAt(0).toString();
    }
  }

  if (isCustomBackgroundColor) {
    // For some reason selected text is lost here
    // Note: This handles custom color picker (hex input), not CSS variable colors from palette

    if (self.selectedSearchText && self.selectedSearchRange) {
      const textElement = self.selection.element();
      const backgroundColor = window.getComputedStyle(textElement).backgroundColor;

      // Check if the selection is already wrapped in a span
      const isExistingSpan = textElement &&
        textElement.tagName === 'SPAN' &&
        textElement.textContent === self.selectedSearchText;

      if (isExistingSpan) {
        // Update the background color on the existing span
        textElement.style.backgroundColor = backgroundColor;
      } else {
        // Create a new span with the selected color
        const span = document.createElement('span');
        span.style.backgroundColor = backgroundColor;
        span.textContent = self.selectedSearchText;

        // Replace selected text with the span
        self.selectedSearchRange.deleteContents();
        self.selectedSearchRange.insertNode(span);
      }

      // Clear the selection
      self.selection.get().removeAllRanges();
      self.selectedSearchText = null
      self.selectedSearchRange = null
    }

  }

  if (isCustomTextColor) {
    // For some reason selected text is lost here
    // Note: This handles custom color picker (hex input), not CSS variable colors from palette

    if (self.selectedSearchText && self.selectedSearchRange) {
      const textElement = self.selection.element();
      const textColor = window.getComputedStyle(textElement).color;

      // Check if the selection is already wrapped in a span
      const isExistingSpan = textElement &&
        textElement.tagName === 'SPAN' &&
        textElement.textContent === self.selectedSearchText;

      if (isExistingSpan) {
        // Update the text color on the existing span
        textElement.style.color = textColor;
      } else {
        // Create a new span with the selected color
        const span = document.createElement('span');
        span.style.color = textColor;
        span.textContent = self.selectedSearchText;

        // Replace selected text with the span
        self.selectedSearchRange.deleteContents();
        self.selectedSearchRange.insertNode(span);
      }

      // Clear the selection
      self.selection.get().removeAllRanges();
      self.selectedSearchText = null
      self.selectedSearchRange = null
    }
  }
}